import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './cn.js';

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-3 p-4 pb-2', className)}>
      <div>
        <h2 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h2>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-4 pt-2', className)} {...props} />;
}

/** KPI kartīte dashboard joslai: liela vērtība + apzīmējums + fakultatīva ikona/akcents. */
export function StatCard({
  label,
  value,
  icon,
  tone = 'default',
  onClick,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  icon?: ReactNode;
  tone?: 'default' | 'warn' | 'danger' | 'ok';
  onClick?: () => void;
  className?: string;
}) {
  const toneCls = {
    default: 'text-slate-900 dark:text-white',
    warn: 'text-amber-600 dark:text-amber-400',
    danger: 'text-red-600 dark:text-red-400',
    ok: 'text-emerald-600 dark:text-emerald-400',
  }[tone];
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm dark:border-slate-800 dark:bg-slate-900',
        onClick &&
          'transition-colors hover:border-brand-300 hover:shadow dark:hover:border-brand-700',
        className,
      )}
    >
      {icon ? (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {icon}
        </div>
      ) : null}
      <div className="min-w-0">
        <div className={cn('text-2xl font-bold leading-tight', toneCls)}>{value}</div>
        <div className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
          {label}
        </div>
      </div>
    </Comp>
  );
}
